import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type { ChampMetier } from "../../types/champMetier";

interface UseGetPosteChampsMetierOptions
  extends Omit<UseQueryOptions<ChampMetier[], Error>, "queryKey" | "queryFn"> {
  posteId: number;
}

/**
 * Hook to fetch ChampMetier (business fields) for a specific poste
 * GET /iam/postes-emission/{posteId}/champs/
 */
export const useGetPosteChampsMetier = ({
  posteId,
  ...queryOptions
}: UseGetPosteChampsMetierOptions) => {
  return useQuery<ChampMetier[], Error>({
    queryKey: ["posteChampsMetier", posteId],
    queryFn: async () => {
      console.log(`[useGetPosteChampsMetier] Fetching champs métier for poste ID: ${posteId}`);

      const response = await fetchClient<ChampMetier[]>(`/iam/postes-emission/${posteId}/champs/`);

      if (response.error) {
        console.error(`[useGetPosteChampsMetier] API Error:`, response.error);

        if (response.error.detail) {
          throw new Error(response.error.detail);
        }

        if (response.error.non_field_errors && response.error.non_field_errors.length > 0) {
          throw new Error(response.error.non_field_errors.join(", "));
        }

        // Handle 404
        if (response.status === 404) {
          console.warn(
            `[useGetPosteChampsMetier] Poste ${posteId} not found, returning empty array`
          );
          return [];
        }

        throw new Error(
          `Failed to fetch champs métier for poste ${posteId}: ${JSON.stringify(response.error)}`
        );
      }

      if (!response.data) {
        console.warn(`[useGetPosteChampsMetier] No data received for poste ${posteId}`);
        return [];
      }

      if (!Array.isArray(response.data)) {
        console.error(
          `[useGetPosteChampsMetier] Invalid data format - expected array, got:`,
          typeof response.data
        );
        throw new Error(`Invalid data format received for poste ${posteId}`);
      }

      console.log(
        `[useGetPosteChampsMetier] Successfully fetched ${response.data.length} champs métier for poste ${posteId}`
      );
      return response.data;
    },
    enabled: !!posteId,
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, error) => {
      if (error.message.includes("404")) {
        return false;
      }
      return failureCount < 2;
    },
    ...queryOptions,
  });
};
